/**
 * Audit Logs API Hook
 * Fetches audit logs from the logs-history API endpoint
 */

import { useFetch, buildUrl } from './useApi';
import { ADMIN_API_BASE_URL } from '../config';
import { getAuthToken } from './useAdminRefactored';

export interface AuditLogEntry {
  id: number;
  action: string;
  entity: string;
  entityId: number;
  status: string;
  logMessage: string;
  createdBy: string;
  createdDate: string;
  createdSystemIp: string;
  isDeletedValue: boolean;
  modifiedBy: string;
  modifiedDate: string;
  modifiedSystemIp: string;
}

export interface AuditLogsPageInfo {
  size: number;
  number: number;
  totalElements: number;
  totalPages: number;
}

export interface AuditLogsResponse {
  status: string;
  httpStatus: string;
  message: string;
  data: {
    content: AuditLogEntry[];
    page: AuditLogsPageInfo;
  };
}

export interface AuditLogsParams {
  page?: number;
  size?: number;
  sort?: string[];
}

/**
 * Get authentication headers
 */
function getAuthHeaders(): Record<string, string> {
  const token = getAuthToken();
  // return token ? { Authorization: `Bearer ${token}` } : {};
  return {};
}

/**
 * Hook to fetch audit logs list
 * GET /logs-history/list
 */
export function useAuditLogs(
  params: AuditLogsParams = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: AuditLogsResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use the base URL from config, or fallback to the direct URL if needed
  // The API endpoint is at https://ride.aboostotransport.com:8082/logs-history/list
  // If ADMIN_API_BASE_URL doesn't match, we may need to configure it differently
  const baseUrl = ADMIN_API_BASE_URL;
  
  // Build query parameters
  const queryParams: Record<string, any> = {
    page: params.page ?? 0,
    size: params.size ?? 25,
  };
  
  // Handle sort parameter - API expects it as a JSON stringified array
  // The curl example shows: sort=%5B%22string%22%5D which decodes to ["string"]
  // This seems to be a placeholder. We'll use it as-is if provided, otherwise omit it
  if (params.sort && params.sort.length > 0) {
    queryParams.sort = JSON.stringify(params.sort);
  } else {
    // Use the same format as the curl example for now
    queryParams.sort = JSON.stringify(['string']);
  }

  const url = buildUrl(baseUrl, '/logs-history/list', queryParams);

  return useFetch<AuditLogsResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Fetch all audit logs for CSV export (non-hook, imperative)
 * Fetches pages sequentially until all data is retrieved
 */
export async function fetchAllAuditLogs(): Promise<AuditLogEntry[]> {
  const baseUrl = ADMIN_API_BASE_URL;
  const allEntries: AuditLogEntry[] = [];
  let page = 0;
  const size = 500;
  let totalPages = 1;

  while (page < totalPages) {
    const queryParams: Record<string, any> = {
      page,
      size,
      sort: JSON.stringify(['string']),
    };
    const url = buildUrl(baseUrl, '/logs-history/list', queryParams);
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });
    if (!response.ok) throw new Error(`Failed to fetch audit logs page ${page}`);
    const json: AuditLogsResponse = await response.json();
    allEntries.push(...(json.data?.content || []));
    totalPages = json.data?.page?.totalPages ?? 1;
    page++;
  }

  return allEntries;
}
